import React, { useState, useRef } from 'react';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  Eye,
  AlertCircle,
  CheckCircle,
  File,
  X,
} from 'lucide-react';
import { useSyllabus, useUploadSyllabus, useDeleteSyllabus, useUpdateSyllabus } from '../../hooks/useProfessor';
import { uploadAssignmentFile } from '../../services/professorService';
import type { UploadSyllabusDto } from '@/types/professor.types';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import { getFullFileUrl, getDownloadFileUrl } from '@/utils/fileUrl';

interface SyllabusTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted = the live one
  academicYearId?: string;
}

const SyllabusTab: React.FC<SyllabusTabProps> = ({ groupeId, moduleElementId, academicYearId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fetch existing syllabus
  const { data: syllabus, isLoading, refetch } = useSyllabus(groupeId, moduleElementId, academicYearId);
  const uploadMutation = useUploadSyllabus();
  const deleteMutation = useDeleteSyllabus();
  const updateMutation = useUpdateSyllabus();

  // Allowed file types
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  // Process and upload a file
  const processFile = async (file: File) => {
    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Format non supporté. Veuillez utiliser un fichier PDF ou Word (DOC, DOCX).');
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setUploadError('Le fichier est trop volumineux. Taille maximale: 10MB.');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      // Store the file on the server and keep its URL. An inline base64
      // string (the previous approach) could not be opened by the mobile app
      // and bloated every syllabus response.
      const uploaded = await uploadAssignmentFile(file);

      const dto: UploadSyllabusDto = {
        fileName: file.name,
        fileUrl: uploaded.file.url,
        fileType: file.name.endsWith('.pdf') ? 'pdf' : 'docx',
        fileSize: file.size,
      };

      await uploadMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto,
      });

      refetch();
    } catch (error) {
      setUploadError('Erreur lors du téléchargement. Veuillez réessayer.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection from input
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    await processFile(file);
  };

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (deleteMutation.isPending) return; // one request per click
    try {
      setShowDeleteModal(false); // Close modal immediately for better UX
      setUploadError(null);
      await deleteMutation.mutateAsync({ groupeId, moduleElementId });
      // Cache is updated automatically by the mutation's onSuccess handler
    } catch (error) {
      console.error('Delete error:', error);
      setUploadError('Erreur lors de la suppression. Veuillez réessayer.');
    }
  };

  // Handle toggle publish
  const handleTogglePublish = async () => {
    if (!syllabus) return;

    try {
      await updateMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: { isPublished: !syllabus.isPublished },
      });
      refetch();
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get file icon
  const getFileIcon = (fileType: string | null) => {
    if (fileType === 'pdf') {
      return <FileText className="h-12 w-12 text-red-500" />;
    }
    return <File className="h-12 w-12 text-blue-500" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Syllabus du cours</h3>
          <p className="text-sm text-gray-500 mt-1">
            Téléchargez le syllabus de votre cours pour le rendre accessible aux étudiants.
          </p>
        </div>

        <div className="p-6">
          {/* No Syllabus State */}
          {!syllabus || !syllabus.fileUrl ? (
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                isDragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />

              <Upload className={`mx-auto h-12 w-12 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
              <h4 className="mt-4 text-lg font-medium text-gray-900">
                {isDragOver ? 'Déposez le fichier ici' : 'Aucun syllabus téléchargé'}
              </h4>
              <p className="mt-2 text-sm text-gray-500">
                Glissez-déposez un fichier ou cliquez pour sélectionner
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Formats acceptés: PDF, DOC, DOCX (max 10MB)
              </p>

              {isUploading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-blue-600">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Téléchargement en cours...</span>
                </div>
              )}
            </div>
          ) : (
            /* Syllabus Uploaded State */
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* File Preview */}
              <div className="flex-shrink-0 bg-gray-50 rounded-xl p-6 flex flex-col items-center">
                {getFileIcon(syllabus.fileType)}
                <span className="mt-2 text-xs font-medium text-gray-500 uppercase">
                  {syllabus.fileType?.toUpperCase() || 'FICHIER'}
                </span>
              </div>

              {/* File Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 truncate">
                      {syllabus.fileName}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatFileSize(syllabus.fileSize || 0)} • Téléchargé le {formatDate(syllabus.updatedAt)}
                    </p>
                  </div>

                  {/* Publish Status Badge */}
                  <div>
                    {syllabus.isPublished ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        <CheckCircle className="h-3 w-3" />
                        Publié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        <AlertCircle className="h-3 w-3" />
                        Non publié
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  {syllabus.fileUrl && (
                    <>
                      <button
                        onClick={() => setShowPreviewModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Aperçu
                      </button>
                      <a
                        href={getDownloadFileUrl(syllabus.fileUrl)}
                        download={syllabus.fileName}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Télécharger
                      </a>
                    </>
                  )}

                  <button
                    onClick={handleTogglePublish}
                    disabled={updateMutation.isPending}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      syllabus.isPublished
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : syllabus.isPublished ? (
                      <>
                        <AlertCircle className="h-4 w-4" />
                        Dépublier
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Publier
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setShowDeleteModal(true)}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Supprimer
                  </button>
                </div>

                {/* Replace File */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {isUploading ? 'Téléchargement...' : 'Remplacer le fichier'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{uploadError}</span>
            </div>
          )}
        </div>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Le syllabus ne sera visible par les étudiants que s'il est publié.
            Vous pouvez télécharger un fichier maintenant et le publier plus tard.
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <CuteConfirmModal
        isOpen={showDeleteModal}
        title="Supprimer le syllabus"
        message="Cette action est irréversible. Le syllabus sera définitivement supprimé."
        itemName={syllabus?.fileName || undefined}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Preview Modal */}
      {showPreviewModal && syllabus?.fileUrl && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FileText className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{syllabus.fileName}</h3>
                  <p className="text-xs text-gray-500">{formatFileSize(syllabus.fileSize || 0)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={getDownloadFileUrl(syllabus.fileUrl)}
                  download={syllabus.fileName}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Télécharger
                </a>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              {(syllabus.fileType === 'pdf' ||
                syllabus.fileType?.includes('pdf') ||
                syllabus.fileName?.toLowerCase().endsWith('.pdf')) ? (
                <iframe
                  src={getFullFileUrl(syllabus.fileUrl)}
                  className="w-full h-full"
                  title="Aperçu du syllabus"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <File className="h-16 w-16 mb-4 text-gray-400" />
                  <p className="text-lg font-medium">Aperçu non disponible</p>
                  <p className="text-sm mt-1">Les fichiers Word ne peuvent pas être prévisualisés.</p>
                  <a
                    href={getDownloadFileUrl(syllabus.fileUrl)}
                    download={syllabus.fileName}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger pour visualiser
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyllabusTab;
